import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Tag } from "Components/Analyses/_Shared/Tag";
import type { TagColor, TagVariant } from "Components/Analyses/_Shared/Tag";
import { Alert } from "Components/_Shared/Alert";
import { BlSkeleton } from "Components/_Shared/BlSkeleton";
import { Button } from "Components/_Shared/Buttons/Button";
import type { Props as ButtonProps } from "Components/_Shared/Buttons/Button";
import { HStack } from "Components/_Shared/Core/Stack";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { useClientDetail } from "Infrastructure/ApiZfp/AnalyzerApi";
import { RelatedClientDetailType } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  CLIENT_DETAIL_RETRY,
  ClientCardState,
  formatClientPhone,
  formatPersonalIdentificationNumber,
  getClientCardState,
  getLegalRepresentative,
  selectClientDetailClient,
} from "Infrastructure/Utils/ClientUtils";
import { type ReactNode, useContext } from "react";
import { Flex } from "../Core/Flex";
import { CLIENT_CARD_TAG, formatBirthDateWithAge, formatCityAndPostalCode } from "./ClientCardDetail.helpers";
import { ClientCardLegalRepresentative } from "./ClientCardLegalRepresentative";
import { InfoItem } from "./InfoItem";

export type ClientCardRowAction = {
  title: string;
  icon?: ReactNode;
  tag?: {
    label: string;
    color: TagColor;
    variant: TagVariant;
  };
  hint?: string;
  button?: {
    label: string;
    onClick: () => void;
  } & Pick<ButtonProps, "disabled" | "loading" | "color" | "variant">;
};

type Props = {
  clientGuid: string;
  rows: ClientCardRowAction[];
  onClick?: () => void;
  legalRepresentationValid?: boolean;
};

const paperSx = {
  display: "flex",
  flexDirection: "column",
  border: "1px solid",
  borderColor: "grey.200",
  borderRadius: 3,
  overflow: "hidden",
} as const;

export const ClientCardDetail = ({ clientGuid, rows, onClick, legalRepresentationValid }: Props) => {
  const {
    data: client,
    isPending: isClientPending,
    isError: isClientError,
  } = useClientDetail(clientGuid, { query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY } });
  const { readOnly } = useContext(AnalysisDetailContext);
  const formatLocale = useFormatLocale();
  const { t } = useResource();
  const isMinorClientEnabled = useFeatureFlag(FeatureFlag.MinorClient);

  if (isClientPending) {
    return (
      <Paper elevation={0} sx={paperSx}>
        <Box px={Spacing.XL} py={Spacing.L}>
          <HStack spacing={Spacing.L} alignItems="center" minWidth={200}>
            <BlSkeleton variant="circular" width={40} height={40} />
            <Box>
              <BlSkeleton variant="text" width={140} height={20} />
              <BlSkeleton variant="text" width={100} height={16} />
            </Box>
          </HStack>
        </Box>
      </Paper>
    );
  }

  if (isClientError) {
    return <Alert variant="error" text={t(Resources.Clients.ClientCard.Alerts.NotAvailable)} />;
  }

  if (client?.type === RelatedClientDetailType.related) {
    const fullName = getClientFullName({ name: client.firstName, surname: client.surname });
    const birthDateText = formatBirthDateWithAge(client.birthDate, formatLocale, t);
    return (
      <Paper elevation={0} sx={paperSx}>
        <Stack spacing={Spacing.XL} px={Spacing.XL} py={Spacing.XL} sx={{ backgroundColor: (theme) => theme.colors.backgroundLight }}>
          <HStack spacing={Spacing.L} alignItems="center">
            <UserInitialsAvatar firstName={client.firstName} surname={client.surname} variant="large" />
            <Typography variant="body2" fontWeight={600}>
              {fullName}
            </Typography>
          </HStack>
          <HStack pl={Spacing.L}>
            <InfoItem icon={<CalendarTodayOutlinedIcon />} text={birthDateText} />
          </HStack>
        </Stack>
        {rows.map((row, index) => (
          <Box key={`${index}-${row.title}`}>
            <Divider />
            <HStack px={Spacing.XL} py={Spacing.XL} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={Spacing.M}>
              <HStack spacing={Spacing.M} alignItems="center">
                {row.icon && (
                  <Box color="text.secondary" display="flex" alignItems="center" sx={{ "& svg": { fontSize: 18 } }}>
                    {row.icon}
                  </Box>
                )}
                <Typography variant="body2" fontWeight={500}>
                  {row.title}
                </Typography>
              </HStack>
              <HStack spacing={Spacing.M} alignItems="center">
                {row.tag && <Tag title={row.tag.label} color={row.tag.color} variant={row.tag.variant} />}
                {row.button && (
                  <Button
                    onClick={row.button.onClick}
                    disabled={row.button.disabled}
                    loading={row.button.loading}
                    color={row.button.color}
                    variant={row.button.variant}
                  >
                    {row.button.label}
                  </Button>
                )}
              </HStack>
            </HStack>
            {row.hint && (
              <Box px={Spacing.XL} pb={Spacing.XL}>
                <Alert variant="info" text={row.hint} />
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    );
  }

  const fullName =
    getClientFullName({
      titlePrefix: client.titlePrefix,
      name: client.firstName,
      surname: client.surname,
      titlePostfix: client.titlePostfix,
    }).trim() || "—";

  const phone = formatClientPhone(client.phonePrefix, client.phoneNationalNumber);

  const birthDateText = formatBirthDateWithAge(client.birthDate, formatLocale, t);

  const address = formatCityAndPostalCode(client.address);

  const mangoUpCardUrl = "mangoUpCardUrl" in client ? client.mangoUpCardUrl : undefined;

  const legalRepresentative = isMinorClientEnabled ? getLegalRepresentative(client) : undefined;
  // The client detail carries no minor flag - a legal representation that is still valid is what marks a minor
  const isMinor = !!legalRepresentative && legalRepresentationValid === true;

  const cardState = getClientCardState(client);
  const cardTag = CLIENT_CARD_TAG[cardState];

  // A Mango client's data is maintained in MangoUp, so there is nothing editable here - the MangoUp button is the way in
  const isEditable = cardState === ClientCardState.NotInMango;

  const defaultRows: ClientCardRowAction[] = [
    {
      title: t(Resources.Clients.ClientCard.Title),
      tag: { label: t(cardTag.label), color: cardTag.color, variant: "solid" },
      hint: cardState === ClientCardState.Missing ? t(Resources.Clients.ClientCard.MissingHint) : undefined,
    },
  ];

  const allRows = [...defaultRows, ...rows];

  return (
    <Paper elevation={0} sx={paperSx}>
      <Stack px={Spacing.XL} py={Spacing.XL} sx={{ backgroundColor: (theme) => theme.colors.backgroundLight }}>
        <HStack alignItems="flex-start" justifyContent="space-between" gap={Spacing.L} flexWrap="wrap">
          <Box flexShrink={0}>
            <UserInitialsAvatar firstName={client.firstName} surname={client.surname} variant="medium" />
          </Box>

          <Stack spacing={Spacing.L} flex={1} pt={Spacing.M}>
            <HStack spacing={Spacing.M} alignItems="center">
              <Typography variant="body1" fontWeight={600}>
                {fullName}
              </Typography>
              {isMinor && (
                <Flex alignItems="center" height={24} px={Spacing.L} borderRadius={Radius.MAX} bgcolor="primary.light">
                  <Typography variant="caption" fontWeight={700} color="primary.main">
                    {t(Resources.Clients.ClientCard.Minor)}
                  </Typography>
                </Flex>
              )}
            </HStack>
          </Stack>

          <HStack spacing={Spacing.M} alignItems="center" pt={Spacing.XS}>
            {mangoUpCardUrl && (
              <Button
                color="secondary"
                size="small"
                onClick={() => window.open(mangoUpCardUrl, "_blank", "noopener,noreferrer")}
                endAdornment={<LaunchOutlinedIcon fontSize="small" />}
              >
                MangoUp
              </Button>
            )}
            {onClick && !readOnly && isEditable && (
              <Button size="small" color="secondary" onClick={onClick} endAdornment={<EditOutlinedIcon fontSize="small" />}>
                {t(Resources.Common.Edit)}
              </Button>
            )}
          </HStack>
        </HStack>

        <HStack spacing={Spacing.XXXL} flexWrap="wrap" pl={Spacing.XXXL}>
          <Stack spacing={Spacing.M} pl={Spacing.XL}>
            <InfoItem icon={<CalendarTodayOutlinedIcon />} text={birthDateText} />
            <InfoItem icon={<BadgeOutlinedIcon />} text={formatPersonalIdentificationNumber(client.personalIdentificationNumber)} />
            <InfoItem icon={<EmailOutlinedIcon />} text={"email" in client ? client.email : undefined} />
          </Stack>
          <Stack spacing={Spacing.M}>
            <InfoItem icon={<PhoneOutlinedIcon />} text={phone} />
            <InfoItem icon={<LocationOnOutlinedIcon />} text={address} />
          </Stack>
        </HStack>
      </Stack>

      {legalRepresentative && (
        <ClientCardLegalRepresentative representative={legalRepresentative} showAdultWarning={legalRepresentationValid === false} />
      )}

      {allRows.map((row, index) => (
        <Box key={`${index}-${row.title}`}>
          <Divider />
          <HStack px={Spacing.XL} py={Spacing.XL} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={Spacing.M}>
            <HStack spacing={Spacing.M} alignItems="center">
              {row.icon && (
                <Box color="text.secondary" display="flex" alignItems="center" sx={{ "& svg": { fontSize: 18 } }}>
                  {row.icon}
                </Box>
              )}
              <Typography variant="body2" fontWeight={500}>
                {row.title}
              </Typography>
            </HStack>
            <HStack spacing={Spacing.M} alignItems="center">
              {row.tag && <Tag title={row.tag.label} color={row.tag.color} variant={row.tag.variant} />}
              {row.button && (
                <Button
                  onClick={row.button.onClick}
                  disabled={row.button.disabled}
                  loading={row.button.loading}
                  color={row.button.color}
                  variant={row.button.variant}
                >
                  {row.button.label}
                </Button>
              )}
            </HStack>
          </HStack>
          {row.hint && (
            <Box px={Spacing.XL} pb={Spacing.XL}>
              <Alert variant="info" text={row.hint} />
            </Box>
          )}
        </Box>
      ))}
    </Paper>
  );
};
